import * as THREE from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

export type PhysicalSkyHandle = {
  mesh: THREE.Mesh;
  /** Call when sun direction changes (optional — static sun is fine). */
  syncSun: (sunWorld: THREE.Vector3) => void;
  /** Under overpass: haze up, sun softer. */
  setUnderpassBlend: (t: number) => void;
  /** Animate clouds */
  update: (dt: number) => void;
  /** Get the HDRI texture for scene background/environment */
  getTexture: () => THREE.Texture | null;
  /** Load the HDRI and set up scene */
  loadHDRI: (renderer: THREE.WebGLRenderer, scene: THREE.Scene) => void;
};

/**
 * Stylized procedural sky - matches the game's aesthetic with gradient colors
 * and stylized clouds instead of photorealistic HDRI.
 */
export function createPhysicalSky(): PhysicalSkyHandle {
  const skyGeo = new THREE.SphereGeometry(5000, 64, 64);

  const skyMat = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uSunDirection: { value: new THREE.Vector3(0.5, 0.8, 0.3).normalize() },
      uTopColor: { value: new THREE.Color(0x1e90ff) },
      uMidColor: { value: new THREE.Color(0x87ceeb) },
      uHorizonColor: { value: new THREE.Color(0xffe4b5) },
      uSunColor: { value: new THREE.Color(0xffd700) },
      uCloudColor: { value: new THREE.Color(0xffffff) },
      uCloudShadow: { value: new THREE.Color(0xc0c0c0) },
    },
    vertexShader: `
      varying vec3 vWorldPosition;
      varying vec2 vUv;
      
      void main() {
        vec4 worldPos = modelMatrix * vec4(position, 1.0);
        vWorldPosition = worldPos.xyz;
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float uTime;
      uniform vec3 uSunDirection;
      uniform vec3 uTopColor;
      uniform vec3 uMidColor;
      uniform vec3 uHorizonColor;
      uniform vec3 uSunColor;
      uniform vec3 uCloudColor;
      uniform vec3 uCloudShadow;
      
      varying vec3 vWorldPosition;
      varying vec2 vUv;
      
      // Simplex noise functions
      vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
      vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
      vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
      vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
      
      float snoise(vec3 v) {
        const vec2 C = vec2(1.0/6.0, 1.0/3.0);
        const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
        
        vec3 i  = floor(v + dot(v, C.yyy));
        vec3 x0 = v - i + dot(i, C.xxx);
        
        vec3 g = step(x0.yzx, x0.xyz);
        vec3 l = 1.0 - g;
        vec3 i1 = min(g.xyz, l.zxy);
        vec3 i2 = max(g.xyz, l.zxy);
        
        vec3 x1 = x0 - i1 + C.xxx;
        vec3 x2 = x0 - i2 + C.yyy;
        vec3 x3 = x0 - D.yyy;
        
        i = mod289(i);
        vec4 p = permute(permute(permute(
                 i.z + vec4(0.0, i1.z, i2.z, 1.0))
               + i.y + vec4(0.0, i1.y, i2.y, 1.0))
               + i.x + vec4(0.0, i1.x, i2.x, 1.0));
        
        float n_ = 0.142857142857;
        vec3 ns = n_ * D.wyz - D.xzx;
        
        vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
        
        vec4 x_ = floor(j * ns.z);
        vec4 y_ = floor(j - 7.0 * x_);
        
        vec4 x = x_ *ns.x + ns.yyyy;
        vec4 y = y_ *ns.x + ns.yyyy;
        vec4 h = 1.0 - abs(x) - abs(y);
        
        vec4 b0 = vec4(x.xy, y.xy);
        vec4 b1 = vec4(x.zw, y.zw);
        
        vec4 s0 = floor(b0)*2.0 + 1.0;
        vec4 s1 = floor(b1)*2.0 + 1.0;
        vec4 sh = -step(h, vec4(0.0));
        
        vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
        vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
        
        vec3 p0 = vec3(a0.xy, h.x);
        vec3 p1 = vec3(a0.zw, h.y);
        vec3 p2 = vec3(a1.xy, h.z);
        vec3 p3 = vec3(a1.zw, h.w);
        
        vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
        p0 *= norm.x;
        p1 *= norm.y;
        p2 *= norm.z;
        p3 *= norm.w;
        
        vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
        m = m * m;
        return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
      }
      
      float fbm(vec3 p) {
        float f = 0.0;
        f += 0.5000 * snoise(p); p *= 2.01;
        f += 0.2500 * snoise(p); p *= 2.02;
        f += 0.1250 * snoise(p); p *= 2.03;
        f += 0.0625 * snoise(p);
        return f;
      }
      
      void main() {
        vec3 direction = normalize(vWorldPosition);
        float height = direction.y;
        
        // Sky gradient - stylized colors
        vec3 skyColor;
        if (height > 0.0) {
          float t = pow(height, 0.6);
          skyColor = mix(uMidColor, uTopColor, t);
          
          // Add warm horizon glow
          float horizonGlow = 1.0 - smoothstep(0.0, 0.3, height);
          skyColor = mix(skyColor, uHorizonColor, horizonGlow * 0.6);
        } else {
          // Below horizon - darker
          skyColor = mix(uHorizonColor, uMidColor * 0.5, -height);
        }
        
        // Sun glow
        float sunDot = dot(direction, uSunDirection);
        float sunGlow = pow(max(0.0, sunDot), 8.0);
        float sunCore = pow(max(0.0, sunDot), 256.0);
        skyColor += uSunColor * sunGlow * 0.4;
        skyColor += vec3(1.0, 0.95, 0.8) * sunCore * 2.0;
        
        // Stylized clouds - only above horizon
        if (height > 0.05) {
          vec3 cloudPos = direction * 3.0;
          cloudPos.x += uTime * 0.02;
          cloudPos.z += uTime * 0.01;
          
          float cloud = fbm(cloudPos * 1.5);
          cloud = smoothstep(0.1, 0.6, cloud);
          
          // Cloud lighting - brighter on sun side
          float cloudLight = dot(direction, uSunDirection) * 0.5 + 0.5;
          vec3 cloudCol = mix(uCloudShadow, uCloudColor, cloudLight);
          
          // Add slight orange tint to clouds near sun
          cloudCol = mix(cloudCol, uSunColor, sunGlow * 0.3);
          
          // Fade clouds near horizon for depth
          float cloudFade = smoothstep(0.05, 0.4, height);
          cloud *= cloudFade;
          
          skyColor = mix(skyColor, cloudCol, cloud * 0.7);
        }
        
        // Atmospheric scattering effect
        float scatter = pow(1.0 - abs(height), 3.0);
        skyColor += vec3(0.8, 0.6, 0.4) * scatter * 0.15;
        
        // Subtle vignette at edges
        float vignette = 1.0 - pow(abs(height - 0.3), 2.0) * 0.1;
        skyColor *= vignette;
        
        gl_FragColor = vec4(skyColor, 1.0);
      }
    `,
    side: THREE.BackSide,
    depthWrite: false,
  });

  const skyMesh = new THREE.Mesh(skyGeo, skyMat);
  skyMesh.name = 'SkyDome';
  skyMesh.renderOrder = -1000;

  let envTexture: THREE.Texture | null = null;

  const loadHDRI = (renderer: THREE.WebGLRenderer, scene: THREE.Scene) => {
    scene.background = null;

    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();

    const rgbeLoader = new RGBELoader();
    
    rgbeLoader.load(
      '/assets/hdri/blue_sky_1k.hdr',
      (texture) => {
        texture.mapping = THREE.EquirectangularReflectionMapping;
        
        const envMap = pmremGenerator.fromEquirectangular(texture).texture;
        scene.environment = envMap;
        envTexture = envMap;
        
        texture.dispose();
        pmremGenerator.dispose();
      },
      undefined,
      () => {
        const roomEnv = new RoomEnvironment();
        envTexture = pmremGenerator.fromScene(roomEnv, 0.04).texture;
        scene.environment = envTexture;
        pmremGenerator.dispose();
      },
    );
  };

  const syncSun = (sunWorld: THREE.Vector3) => {
    (skyMat.uniforms.uSunDirection.value as THREE.Vector3).copy(sunWorld).normalize();
  };

  const setUnderpassBlend = (_t: number) => {
  };

  let time = 0;
  const update = (dt: number) => {
    time += dt;
    skyMat.uniforms.uTime.value = time;
  };

  const getTexture = () => envTexture;

  return {
    mesh: skyMesh,
    syncSun,
    setUnderpassBlend,
    update,
    getTexture,
    loadHDRI,
  };
}
